import { msalInstance, loginRequest } from 'Configuration/MsalConfiguration';
import F2WCustomLogger from 'Services/F2WCustomLogger';

let tokenRenewalInProgress = false;
let tokenRenewalPromise = null;

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime + 300; // Consider expired if expires in next 5 minutes
  } catch {
    return true;
  }
};

const renewTokenSilently = async () => {
  if (tokenRenewalInProgress) {
    return tokenRenewalPromise;
  }

  tokenRenewalInProgress = true;
  tokenRenewalPromise = (async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No accounts found. Please login.');
      }

      const request = {
        ...loginRequest,
        account: accounts[0],
        forceRefresh: true,
      };

      const response = await msalInstance.acquireTokenSilent(request);
      return response.idToken;
    } finally {
      tokenRenewalInProgress = false;
      tokenRenewalPromise = null;
    }
  })();

  return tokenRenewalPromise;
};

export const getMsalToken = async () => {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('No accounts found. Please login.');
  }

  const request = {
    ...loginRequest,
    account: accounts[0],
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error.name === 'InteractionRequiredAuthError') {
      try {
        const response = await msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      } catch (popupError) {
        F2WCustomLogger.error('Token acquisition failed', popupError, { service: 'MsalToken' });
        throw popupError;
      }
    }
    throw error;
  }
};

export const getMsalAccount = () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    const account = accounts[0];
    const idToken = account.idToken;
    return {
      ...account,
      idToken: idToken,
    };
  }
  return null;
};

const isInteractionError = (error) =>
  error.name === 'InteractionRequiredAuthError' ||
  error.name === 'consent_required' ||
  error.name === 'interaction_required' ||
  error.name === 'login_required';

const acquireTokenInteractively = async (request) => {
  try {
    F2WCustomLogger.log('Attempting token acquisition via popup', { service: 'MsalToken' });
    const response = await msalInstance.acquireTokenPopup(request);
    return response.idToken;
  } catch (popupError) {
    F2WCustomLogger.error('Popup token acquisition failed', popupError, { service: 'MsalToken' });
    F2WCustomLogger.log('Redirecting to login', { service: 'MsalToken' });
    await msalInstance.loginRedirect(request);
    throw popupError;
  }
};

const acquireTokenWithForceRefresh = async (request) => {
  try {
    F2WCustomLogger.log('Retrying with force refresh', { service: 'MsalToken' });
    const retryRequest = { ...request, forceRefresh: true };
    const response = await msalInstance.acquireTokenSilent(retryRequest);
    return response.idToken;
  } catch (retryError) {
    F2WCustomLogger.error('Force refresh failed', retryError, { service: 'MsalToken' });
    return null;
  }
};

export const getMsalIdToken = async () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No accounts found. Please login.');
  }

  const account = accounts[0];
  const currentToken = account.idToken;

  // Check if current token is expired or about to expire
  if (isTokenExpired(currentToken)) {
    try {
      return await renewTokenSilently();
    } catch (renewError) {
      F2WCustomLogger.warn('Silent token renewal failed', {
        error: renewError.name,
        message: renewError.message,
        service: 'MsalToken',
      });
      // Fall through to interactive renewal
    }
  } else if (currentToken) {
    return currentToken;
  }

  const request = {
    ...loginRequest,
    account: account,
    forceRefresh: false,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.idToken;
  } catch (error) {
    F2WCustomLogger.warn('Silent token acquisition failed', {
      error: error.name,
      message: error.message,
      service: 'MsalToken',
    });

    if (isInteractionError(error)) {
      return acquireTokenInteractively(request);
    }

    if (!request.forceRefresh) {
      const token = await acquireTokenWithForceRefresh(request);
      if (token) return token;
    }

    throw error;
  }
};

export const handleTokenExpiration = async () => {
  try {
    await renewTokenSilently();
    return true;
  } catch (error) {
    F2WCustomLogger.error('Token renewal failed, redirecting to login', error, { service: 'MsalToken' });
    await msalInstance.loginRedirect(loginRequest);
    return false;
  }
};

// Legacy compatibility - keep for backward compatibility with existing code
class MsalTokenService {
  async initialize() {
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();
  }

  async getToken() {
    return getMsalIdToken();
  }

  getAccount() {
    return getMsalAccount();
  }

  async login() {
    return msalInstance.loginRedirect(loginRequest);
  }

  async logout() {
    const account = this.getAccount();
    return msalInstance.logoutRedirect({ account });
  }

  getMsalInstance() {
    return msalInstance;
  }
}

export default new MsalTokenService();
